/**
 * **Débito automático clásico de Prisma/Payway por archivo (PrismaNet)** — lo que comparten
 * backend y SPA (decisión del 13-oct-2026, `docs/decisiones/debito-prismanet.md`).
 *
 * El club tiene muchos socios adheridos al débito CLÁSICO: Maru arma con la planilla
 * oficial «Plug&Play Débito Automático v1.3» el archivo de presentación (con el NÚMERO DE
 * TARJETA de cada socio) y lo sube a PrismaNet. El sistema reemplaza la planilla mientras
 * Payway no habilita la tokenización interna (etapa A) y después pasa cada tarjeta a token
 * de Pagos online (etapa B).
 *
 * Acá viven sólo las reglas puras que la pantalla de alta necesita validar EN EL ACTO
 * (dígito verificador, largo, marca por BIN) y los nombres de los archivos. El formato
 * byte a byte de los archivos vive en el backend (`integrations/payway/prismanet`).
 */
/** Los cuatro archivos de presentación: uno por marca y tipo de tarjeta. */
export const PRISMANET_CARD_TYPES = [
    "visa_credit",
    "visa_debit",
    "master_credit",
    "master_debit",
];
export const PRISMANET_CARD_TYPE_LABEL = {
    visa_credit: "Visa crédito",
    visa_debit: "Visa débito",
    master_credit: "Mastercard crédito",
    master_debit: "Mastercard débito",
};
/** Nombre del archivo de presentación de cada tipo (macro de la planilla Plug&Play). */
export const PRISMANET_PRESENTATION_FILE = {
    visa_credit: "DEBLIQC",
    visa_debit: "DEBLIQD",
    master_credit: "DEBLIMC",
    master_debit: "DEBLIMD",
};
export function prismanetNetworkOf(type) {
    return type.startsWith("visa") ? "visa" : "mastercard";
}
export function prismanetIsDebitType(type) {
    return type.endsWith("_debit");
}
export function prismanetCardType(network, kind) {
    return `${network === "visa" ? "visa" : "master"}_${kind}`;
}
/**
 * Archivos que devuelve PrismaNet. `R…C` es el resultado del débito en crédito, `R…D` la
 * validación previa del débito en tarjeta de débito, `L…D` la liquidación del débito en
 * tarjeta de débito y `RNOVDEB…` las novedades que el titular cargó en Prisma.
 */
export const PRISMANET_RESPONSE_KINDS = [
    "RDEBLIQC",
    "RDEBLIMC",
    "RDEBLIQD",
    "RDEBLIMD",
    "LDEBLIQD",
    "LDEBLIMD",
    "RNOVDEBC",
    "RNOVDEBM",
];
export const PRISMANET_RESPONSE_LABEL = {
    RDEBLIQC: "Resultado Visa crédito",
    RDEBLIMC: "Resultado Mastercard crédito",
    RDEBLIQD: "Validación Visa débito",
    RDEBLIMD: "Validación Mastercard débito",
    LDEBLIQD: "Liquidación Visa débito",
    LDEBLIMD: "Liquidación Mastercard débito",
    RNOVDEBC: "Novedades Visa",
    RNOVDEBM: "Novedades Mastercard",
};
/** La red de un archivo de rendición; el tipo de tarjeta cuando el archivo lo fija. */
export function prismanetResponseScope(kind) {
    switch (kind) {
        case "RDEBLIQC":
            return { network: "visa", cardType: "visa_credit" };
        case "RDEBLIMC":
            return { network: "mastercard", cardType: "master_credit" };
        case "RDEBLIQD":
        case "LDEBLIQD":
            return { network: "visa", cardType: "visa_debit" };
        case "RDEBLIMD":
        case "LDEBLIMD":
            return { network: "mastercard", cardType: "master_debit" };
        case "RNOVDEBC":
            return { network: "visa", cardType: null };
        case "RNOVDEBM":
            return { network: "mastercard", cardType: null };
    }
}
/** Reconoce el tipo de rendición por el nombre del archivo (`RDEBLIQC.txt`, `rdebliqc_0910.TXT`). */
export function prismanetResponseKindFromName(fileName) {
    const upper = fileName.toUpperCase();
    return PRISMANET_RESPONSE_KINDS.find((kind) => upper.includes(kind)) ?? null;
}
/** Reconoce el tipo de presentación por el nombre del archivo (`DEBLIQC.txt`). */
export function prismanetCardTypeFromFileName(fileName) {
    const upper = fileName.toUpperCase();
    // `RDEBLIQC` contiene `DEBLIQC`: una rendición no es una presentación.
    if (prismanetResponseKindFromName(upper))
        return null;
    const found = PRISMANET_CARD_TYPES.find((type) => upper.includes(PRISMANET_PRESENTATION_FILE[type]));
    return found ?? null;
}
// ── Validaciones de la tarjeta en el alta (agregado del 16-oct) ─────────────
/** El campo del archivo es de 16 posiciones: es el largo que se acepta. */
export const PRISMANET_PAN_LENGTH = 16;
/** Dígito verificador (Luhn). Sólo dígitos; cualquier otro carácter ⇒ `false`. */
export function luhnValid(digits) {
    if (!/^\d+$/.test(digits) || digits.length < 2)
        return false;
    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let d = digits.charCodeAt(i) - 48;
        if (double) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        double = !double;
    }
    return sum % 10 === 0;
}
/** Visa empieza con 4; Mastercard con 51–55 o 2221–2720. Otra red ⇒ `null`. */
export function cardNetworkFromBin(digits) {
    if (/^4/.test(digits))
        return "visa";
    if (/^5[1-5]/.test(digits))
        return "mastercard";
    const four = Number(digits.slice(0, 4));
    if (digits.length >= 4 && four >= 2221 && four <= 2720)
        return "mastercard";
    return null;
}
/** ¿El BIN está en la lista de BIN de débito (`debit.prismanet.debitBins`)? */
export function binIsDebit(digits, debitBins) {
    return debitBins.some((prefix) => prefix.length > 0 && digits.startsWith(prefix));
}
/**
 * Valida el número de tarjeta para el débito por archivo, que **no la valida en el acto**
 * (Payway la valida recién al procesar la presentación): largo, dígito verificador, red
 * por BIN (Visa o Mastercard) y tipo. El tipo lo declara quien carga la tarjeta; si el BIN
 * está en la lista de débito del club y se declaró crédito (o al revés con un BIN de
 * débito conocido), se rechaza: iría al archivo equivocado.
 */
export function checkPrismanetCard(input) {
    const pan = input.pan.replace(/[\s-]/g, "");
    const errors = [];
    if (!/^\d+$/.test(pan)) {
        errors.push("El número de tarjeta lleva sólo dígitos.");
        return { ok: false, errors, network: null, cardType: null };
    }
    if (pan.length !== PRISMANET_PAN_LENGTH) {
        errors.push(`El número de tarjeta tiene que tener ${PRISMANET_PAN_LENGTH} dígitos.`);
    }
    if (!luhnValid(pan)) {
        errors.push("El número de tarjeta no es válido: revisá que no haya un dígito mal tipeado.");
    }
    const network = cardNetworkFromBin(pan);
    if (!network) {
        errors.push("El débito por archivo acepta sólo tarjetas Visa o Mastercard.");
    }
    const binDebit = binIsDebit(pan, input.debitBins);
    if (binDebit && input.declaredKind === "credit") {
        errors.push("Esa tarjeta es de débito según su BIN: elegí «débito».");
    }
    const cardType = network
        ? prismanetCardType(network, binDebit ? "debit" : input.declaredKind)
        : null;
    return { ok: errors.length === 0, errors, network, cardType };
}
/** El identificador del débito: 15 dígitos con ceros a la izquierda (campo del archivo). */
export const PRISMANET_IDENTIFIER_LENGTH = 15;
export function normalizeDebitIdentifier(raw) {
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 0 || digits.length > PRISMANET_IDENTIFIER_LENGTH)
        return null;
    return digits.padStart(PRISMANET_IDENTIFIER_LENGTH, "0");
}
// ── Adhesión, presentación y rendición ──────────────────────────────────────
/**
 * Por dónde se cobra la adhesión: `api` = día a día con el token de Pagos online (VTCS-58/59);
 * `prismanet` = débito clásico por archivo, con la tarjeta en la bóveda hasta la etapa B.
 */
export const DEBIT_CHANNELS = ["api", "prismanet"];
/** `generated` → Maru la sube → `uploaded`; con todo rendido → `closed`; `voided` libera las cuotas. */
export const PRISMANET_PRESENTATION_STATUSES = [
    "generated",
    "uploaded",
    "closed",
    "voided",
];
/** Una presentación abierta todavía puede rendir: sus cuotas no salen en otra. */
export const PRISMANET_OPEN_STATUSES = [
    "generated",
    "uploaded",
];
/**
 * Estado de cada registro presentado. `validated` = la validación del débito en tarjeta de
 * débito la dio por buena y falta la liquidación; `retrying` = Prisma reintenta sola la
 * autorización (código 034 de la liquidación).
 */
export const PRISMANET_RECORD_RESULTS = [
    "pending",
    "validated",
    "retrying",
    "accepted",
    "rejected",
];
/** Tarjetas de la bóveda: `active`, en revisión (sin socio) o purgadas. */
export const CARD_VAULT_STATUSES = ["active", "pending_review", "discarded", "purged"];
/** Por qué una tarjeta quedó para revisión a mano. */
export const CARD_VAULT_REVIEW_REASONS = [
    "no_match",
    "ambiguous_match",
    "no_active_plan",
    "already_adhered",
    "novelty_alta",
];
//# sourceMappingURL=prismanet.js.map